/**
 * SG Incorporation (Pre) — live UI 문자열 → Word [[if …]] 용 값 정규화.
 * Word 템플릿: `[[if b1_q1]]` / `[[if not b1_q1]]` / `[[if srv_a_1]]` /
 * `[[if has_corporate_shareholder]]` / `[[if b3_q1 == "yes"]]` 등.
 * 문자열 'no' 는 JS 에서 truthy 라 체크가 반대로/미반영되므로 boolean 으로 변환.
 *
 * 또한 Studio 행 키(dir1_director_email …) → Word bare(director_email) 복사,
 * Corp SH 표 d1_* / s1_* → cs_directors[] / cs_shareholders[] 배열 생성.
 */
import { personAddressFallback } from '../incorporation-pre/cdd/syncAuraDocs';

export function toWordBool(v: unknown): boolean {
  if (v === true || v === 1) return true;
  if (v === false || v === 0 || v == null || v === '') return false;
  const s = String(v).trim().toLowerCase();
  if (['yes', 'true', '1', 'y', 'checked'].includes(s)) return true;
  if (['no', 'false', '0', 'n', 'unchecked', 'na'].includes(s)) return false;
  return Boolean(s);
}

const BOOL_EXACT = new Set([
  'has_corporate_shareholder',
  'has_pep',
  'ihc_is_public',
  'upc_is_public',
  'cs_is_public_listed',
  'has_managing_directors',
  'has_ceos',
  'has_partners',
  'has_others',
  'has_ubos',
  'has_pre_1993_history',
  'is_employment_pass_holder',
  'needs_interpreter',
  'uses_passport',
  // Aura DDF — 이해관계 구분 조건 ([[if own_name_interests]] … 등)
  'other_interests',
  'own_name_interests',
  'family_interests',
  'deemed_interests',
  'corporate_interests',
  // Aura Incorporation Documents 번들 — 체크박스/선택 ([[if X]]X[[endif]])
  'svc_secretarial', 'svc_registered_office', 'svc_nominee_director', 'svc_nominee_shareholder',
  'audit_appoint', 'audit_arrange', 'bookkeeping_arrange',
  'apply_gst', 'apply_cr', 'apply_license',
  'roc_at_company', 'roc_at_agent', 'rond_at_company', 'rond_at_agent',
  'rons_at_company', 'rons_at_agent',
  'mail_collection', 'mail_forward', 'mail_scan',
  // ACRA Risk Assessment (RFA 2025) — Regulation 24(1) 제3자 의존 문단 ([[if relies_on_third_party]])
  'relies_on_third_party',
  // Notice for Controller (Corporate) / Nominee Director / Nominee Shareholder — Yes/No 체크
  'is_controller', 'knows_other_controller', 'is_nominee_director', 'is_nominee_shareholder',
]);

function isBoolKey(k: string): boolean {
  if (BOOL_EXACT.has(k)) return true;
  if (/^srv_a_\d+$/.test(k)) return true;
  if (/^(dir|sh|ubo)\d+_is_pep$/.test(k)) return true;
  return false;
}

/** b1/b2/b3 는 `== "yes"|"no"|"na"` 비교용 문자열 유지.
 *  (ACRA RFA 2025 템플릿은 Yes·No 칸 모두 값 비교 — 미응답이면 둘 다 ☐. boolean 으로 바꾸면 비교가 안 맞는다) */
function normalizeB3(v: unknown): string {
  const s = String(v ?? '').trim().toLowerCase();
  if (s === 'yes' || s === 'true' || s === '1') return 'yes';
  if (s === 'no' || s === 'false' || s === '0') return 'no';
  if (s === 'na' || s === 'n/a') return 'na';
  return '';
}

/** 값을 트림된 문자열로. null/undefined 는 '' */
function txt(v: unknown): string {
  return v == null ? '' : String(v).trim();
}

/**
 * 반복 배열을 만들 때 훑을 행 인덱스.
 *
 * 화면(SgClientAcceptanceDoc)이 `caf_ids_pep = "1,2,4,5"` 처럼 **실제로 떠 있는 행**을
 * 알려 주면 그대로 쓴다. 행을 지웠다 넣는 과정에서 목록에서는 빠졌는데 값만 남은
 * 유령 인덱스가 생기면, 단순히 1..N 을 훑을 경우 화면에 없는 사람이 문서에 찍힌다.
 * (알림이 없으면 기존처럼 1..max 스캔 — 다른 문서/이전 데이터 호환)
 */
function rowIds(out: Record<string, any>, prefix: string, max = 30): number[] {
  const raw = txt(out[`caf_ids_${prefix}`]);
  if (raw) {
    const ids = raw.split(',').map((s) => Number(s.trim())).filter((n) => Number.isFinite(n) && n > 0);
    if (ids.length) return ids;
  }
  return Array.from({ length: max }, (_, i) => i + 1);
}

function isFilled(v: unknown): boolean {
  return v != null && String(v).trim() !== '';
}

/**
 * dir1_director_email → director_email (첫 행 우선).
 * md1_md_name → md_name, ot1_o_role → o_role 등.
 */
/**
 * 최상위로 접으면 **다른 문서를 오염시키는** 키.
 *
 * 예) `dir1_execution_day` 가 최상위 `execution_day` 가 되면, Form 45 전용 실행일자가
 *     First Board·Share Application·Notice 등 모든 문서의 [[Dated_On]] 계산에 끼어든다
 *     (실제로 전 문서가 "20 day of July, 2026" 으로 찍힌 사례).
 *     이 키들은 해당 인물 문서 스코프 안에서만 의미가 있다.
 */
const NO_FLATTEN = new Set([
  'execution_day', 'execution_month', 'execution_year', 'execution_date',
  'appointment_effective_date', 'consent_effective_date',
  'needs_interpreter', 'interpreter_language', 'interpreter_name', 'interpreter_nric',
  'has_pre_1993_history', 'is_employment_pass_holder', 'uses_passport',
  'dated_on',
]);

function flattenIndexedPersonRows(out: Record<string, any>) {
  const INDEXED = /^(dir|sh|ubo|pep|md|ceo|pa|ot|sec)(\d+)_(.+)$/;
  const best = new Map<string, { idx: number; val: unknown }>();

  for (const [k, v] of Object.entries(out)) {
    if (k.includes('/') || !isFilled(v)) continue;
    const m = k.match(INDEXED);
    if (!m) continue;
    const idx = Number(m[2]);
    const bare = m[3];
    if (!bare || NO_FLATTEN.has(bare)) continue;
    const prev = best.get(bare);
    if (!prev || idx < prev.idx) best.set(bare, { idx, val: v });
  }

  for (const [bare, { val }] of best) {
    if (!isFilled(out[bare])) out[bare] = val;
  }

  // 일부 템플릿은 d_name 별칭 사용
  if (!isFilled(out.d_name) && isFilled(out.director_name)) {
    out.d_name = out.director_name;
  }
}

/** Corp SH Profile — flat dN_* / sN_* → Word 반복 배열 */
function buildCorporateShareholderArrays(out: Record<string, any>) {
  const hasDirs = Array.isArray(out.cs_directors) && out.cs_directors.length > 0;
  if (!hasDirs) {
    const dirs: Record<string, any>[] = [];
    for (let i = 1; i <= 50; i++) {
      if (
        !isFilled(out[`d${i}_name`]) &&
        !isFilled(out[`d${i}_identification_no`]) &&
        !isFilled(out[`d${i}_nationality`])
      ) {
        continue;
      }
      dirs.push({
        no: isFilled(out[`d${i}_no`]) ? out[`d${i}_no`] : i,
        name: out[`d${i}_name`] || '',
        nationality: out[`d${i}_nationality`] || '',
        identification_no: out[`d${i}_identification_no`] || '',
        residential_address: out[`d${i}_residential_address`] || '',
        date_of_appointment: out[`d${i}_date_of_appointment`] || '',
      });
    }
    if (dirs.length) out.cs_directors = dirs;
  }

  const hasShs = Array.isArray(out.cs_shareholders) && out.cs_shareholders.length > 0;
  if (!hasShs) {
    const shs: Record<string, any>[] = [];
    for (let i = 1; i <= 50; i++) {
      if (
        !isFilled(out[`s${i}_name`]) &&
        !isFilled(out[`s${i}_identification_no`]) &&
        !isFilled(out[`s${i}_no_of_shares_and_percentage`])
      ) {
        continue;
      }
      shs.push({
        no: isFilled(out[`s${i}_no`]) ? out[`s${i}_no`] : i,
        name: out[`s${i}_name`] || '',
        nationality: out[`s${i}_nationality`] || '',
        identification_no: out[`s${i}_identification_no`] || '',
        residential_address: out[`s${i}_residential_address`] || '',
        date_of_appointment: out[`s${i}_date_of_appointment`] || '',
        no_of_shares_and_percentage: out[`s${i}_no_of_shares_and_percentage`] || '',
      });
    }
    if (shs.length) out.cs_shareholders = shs;
  }
}

/**
 * formData 를 Word 렌더용으로 복사·정규화.
 * 체크 관련 키는 항상 boolean(또는 b3 문자열)으로 넣고, 미체크 srv/b1/b2 도 false 로 채움.
 */
export function normalizeSgPreFormDataForWord(
  form: Record<string, any>,
): Record<string, any> {
  const out: Record<string, any> = { ...form };

  for (const [k, v] of Object.entries(out)) {
    const bare = k.includes('/') ? k.slice(k.indexOf('/') + 1) : k;
    if (/^b[123]_q\d+$/.test(bare)) {
      out[k] = normalizeB3(v);
      continue;
    }
    if (isBoolKey(bare)) {
      out[k] = toWordBool(v);
    }
  }

  // Word `if not srv_a_N` / `if not b1_qN` 가 undefined 에 의존하지 않도록 기본 false 채움
  for (let i = 1; i <= 11; i++) {
    const k = `srv_a_${i}`;
    if (!(k in out)) out[k] = false;
    else out[k] = toWordBool(out[k]);
  }
  // ACRA RFA 2025 — B1 19행(4a/4b 포함) · B2 4 · B3 11 (미응답은 '' → Yes/No 둘 다 ☐)
  for (const [prefix, n] of [['b1', 19], ['b2', 4], ['b3', 11]] as const) {
    for (let i = 1; i <= n; i++) {
      const k = `${prefix}_q${i}`;
      if (!(k in out) || out[k] === '') continue;
      out[k] = normalizeB3(out[k]);
    }
  }

  for (const k of BOOL_EXACT) {
    if (k in out) out[k] = toWordBool(out[k]);
  }

  for (const k of [
    'has_pre_1993_history',
    'is_employment_pass_holder',
    'needs_interpreter',
    'uses_passport',
  ]) {
    if (!(k in out)) out[k] = false;
    else out[k] = toWordBool(out[k]);
  }

  // angular `if recommendation == "recommended"` 등 — 소문자 정규화
  if (out.recommendation) out.recommendation = String(out.recommendation).toLowerCase();
  if (out.approval) out.approval = String(out.approval).toLowerCase();
  if (out.customer_risk_rating) {
    out.customer_risk_rating = String(out.customer_risk_rating).toLowerCase();
    out.risk_rating = out.customer_risk_rating;
  }

  flattenIndexedPersonRows(out);
  buildCorporateShareholderArrays(out);
  buildAuraTemplateAliases(out);
  buildAcraCheckboxes(out);

  // 템플릿 표기를 SG 방식(소문자 스네이크)으로 통일했으므로 대소문자 사본을 만들지 않는다.
  //   예전 Aura 템플릿은 [[Director_Name]] 처럼 대문자라 우리 키 director_name 으로는
  //   하나도 치환되지 않았고, 그래서 키마다 사본을 만들어 함께 보냈다
  //   (문서당 변수가 두 배로 불어나 치환이 느려지는 주원인이었다).
  //   템플릿을 [[director_name]] 으로 바꾼 뒤로는 필요 없다.
  //   ⚠ 앞으로 올라오는 Aura 템플릿도 **소문자 스네이크 표기**여야 한다.
  //      대문자 표기가 섞이면 그 자리는 에러 없이 빈칸으로 나온다.

  return out;
}

/**
 * Aura(.docx) 템플릿 변수명 별칭 생성.
 *
 * 문서 컴포넌트는 SG 복사본을 재사용해 `cs_*` 같은 SG 키를 쓰는데,
 * Aura 가 업로드한 실제 템플릿은 다른 이름을 쓴다 → 치환이 하나도 안 되는 문서가 생긴다.
 *   예) 템플릿 [[Corporate_Shareholder_Name]]  vs  우리 키 cs_company_name  → 0/14
 * 백엔드 치환은 **대소문자를 무시**하므로(documentVariableReplacer: k.toLowerCase() 비교)
 * 여기서는 소문자 스네이크로만 별칭을 넣어 주면 된다.
 *
 * 또한 `[[if foreign_persons]] … [[/if]]` 같은 **반복 블록**은 배열이 필요하다.
 */
function buildAuraTemplateAliases(out: Record<string, any>) {
  const alias = (target: string, source: string) => {
    if (!isFilled(out[target]) && isFilled(out[source])) out[target] = out[source];
  };

  // ── Incorporation Documents 수수료 : Engagement Letter 화면에서 입력한 값을 재사용 ──
  //   같은 금액을 두 문서에 두 번 입력하지 않도록, Incorporation Documents 화면을
  //   비워 두면 Engagement 값을 그대로 쓴다(화면에 직접 입력하면 그 값이 우선 — alias 규칙).
  //   ⚠ '노미니 비서료(fee_nominee_secretary_annual)' 와 compilation(active/dormant) 은
  //      Engagement 에 대응 항목이 없어 자동 연결하지 않는다(문서 화면에서 직접 입력).
  alias('fee_secretarial_annual', 'fee_annual_secretarial');
  alias('fee_nominee_director_annual', 'fee_annual_nominee_director');
  alias('fee_registered_address_annual', 'fee_annual_registered_address');
  // fee_nominee_director_deposit 는 Engagement 와 키 이름이 같아 그대로 들어온다.

  // ── Corporate Shareholder Profile : cs_* → corporate_shareholder_* ──
  const CS_MAP: Record<string, string> = {
    corporate_shareholder_name: 'cs_company_name',
    corporate_shareholder_registration_no: 'cs_registration_no',
    corporate_shareholder_former_name: 'cs_former_name',
    corporate_shareholder_country_of_incorporation: 'cs_country_of_incorporation',
    corporate_shareholder_date_of_incorporation: 'cs_incorporation_date',
    corporate_shareholder_business_activity: 'cs_business_activity',
    corporate_shareholder_paid_up_capital: 'cs_paid_up_capital',
    corporate_shareholder_number_of_shares_issued: 'cs_no_of_shares_issued',
    corporate_shareholder_registered_address: 'cs_registered_address',
    corporate_shareholder_last_financial_year: 'cs_last_financial_year',
    corporate_shareholder_website: 'cs_website',
    corporate_shareholder_contact_person: 'cs_contact_person',
    corporate_shareholder_contact_no_office: 'cs_contact_no_office',
    corporate_shareholder_email: 'cs_email',
    corporate_shareholder_mobile_no: 'cs_mobile_no',
  };
  for (const [target, source] of Object.entries(CS_MAP)) alias(target, source);

  // 반복 블록: [[if corporate_shareholder_directors]] / [[if corporate_shareholder_shareholders]]
  if (Array.isArray(out.cs_directors) && out.cs_directors.length
      && !Array.isArray(out.corporate_shareholder_directors)) {
    out.corporate_shareholder_directors = out.cs_directors.map((d: any) => ({
      corporate_shareholder_director_name: d.name || '',
      corporate_shareholder_director_nationality: d.nationality || '',
      corporate_shareholder_director_identification_no: d.identification_no || '',
      corporate_shareholder_director_residential_address: d.residential_address || '',
      corporate_shareholder_director_date_of_appointment: d.date_of_appointment || '',
    }));
  }
  if (Array.isArray(out.cs_shareholders) && out.cs_shareholders.length
      && !Array.isArray(out.corporate_shareholder_shareholders)) {
    out.corporate_shareholder_shareholders = out.cs_shareholders.map((s: any) => ({
      corporate_shareholder_shareholder_name: s.name || '',
      corporate_shareholder_shareholder_nationality: s.nationality || '',
      corporate_shareholder_shareholder_identification_no: s.identification_no || '',
      corporate_shareholder_shareholder_residential_address: s.residential_address || '',
      corporate_shareholder_shareholder_number_of_shares_and_percentage:
        s.no_of_shares_and_percentage || '',
      corporate_shareholder_shareholder_date_of_appointment: s.date_of_appointment || '',
    }));
  }

  // ── XX Details of Foreign Director or Shareholder : fp{N}_* → foreign_persons[] ──
  //   템플릿: [[if foreign_persons]] [[Foreign_Person_Name]] … [[/if foreign_persons]]
  //   백엔드는 formData 의 배열을 자동으로 반복 섹션(repeatingData)으로 등록하고
  //   expandRepeatingSections 가 행을 복제한다. 배열만 정확히 넘기면 N명 모두 렌더된다.
  //   (단 documentVariableReplacer 의 REPEAT_LOOP_CONDITION_NAMES 에 이름이 등록돼 있어야
  //    조건부 단계에서 블록이 먼저 삭제되지 않는다 — 등록 완료)
  if (!Array.isArray(out.foreign_persons)) {
    const rows: Record<string, any>[] = [];
    for (let i = 1; i <= 50; i++) {
      const name = out[`fp${i}_foreign_person_name`];
      const pass = out[`fp${i}_foreign_person_passport_fin_no`];
      if (!isFilled(name) && !isFilled(pass)) continue;
      rows.push({
        // 템플릿 No. 컬럼 [[NO]] — 표시 순번(1,2,3…). 대문자 표기라 소문자 사본도 함께.
        NO: rows.length + 1,
        no: rows.length + 1,
        foreign_person_name: name || '',
        foreign_person_passport_fin_no: pass || '',
        foreign_person_date_of_expiry: out[`fp${i}_foreign_person_date_of_expiry`] || '',
        foreign_person_action: out[`fp${i}_foreign_person_action`] || '',
      });
    }
    if (rows.length) out.foreign_persons = rows;
  }
  // ※ 첫 행 값을 최상위 키로 승격하지 않는다 —
  //   승격하면 반복 확장으로 복제된 모든 행이 1번 사람 값으로 덮일 수 있다.

  // ── DDF 이해관계 : 조건 플래그가 아니라 **반복 섹션(행 배열)** 이다 ──────────
  //   템플릿의 [[if own_name_interests]] … [[/if own_name_interests]] 는 표의 한 행을
  //   감싸고 있고, 백엔드 extractRepeatingSections 가 이를 "반복 섹션"으로 인식한다
  //   (편집 화면에도 `Own_name_interests  1 row` 처럼 표로 뜬다).
  //   따라서 'yes'/'no' 문자열을 보내면 **0 rows** 로 처리돼 행이 통째로 사라지고
  //   입력한 Registered Holder·금액도 함께 없어진다. → 반드시 배열로 변환한다.
  const interestRow = {
    registered_holder: out.registered_holder || '',
    interest_description: out.interest_description || '',
    amount_at_appointment: out.amount_at_appointment || '',
    amount_at_end: out.amount_at_end || '',
    date_price_details: out.date_price_details || '',
  };
  for (const sec of [
    'own_name_interests',
    'family_interests',
    'deemed_interests',
    'corporate_interests',
  ]) {
    if (Array.isArray(out[sec])) continue;
    out[sec] = toWordBool(out[sec]) ? [{ ...interestRow }] : [];
  }
  if (!Array.isArray(out.other_interests)) {
    out.other_interests = toWordBool(out.other_interests)
      ? [{
          corporation_name: out.corporation_name || '',
          interest_nature: out.interest_nature || '',
        }]
      : [];
  }

  alias('client_title', 'client_designation');

  // ── Client Acceptance Form : 지배구조·PEP·FYE 이름 보정 ──────────────────
  //   템플릿은 Holding_Company_* / Ultimate_Parent_* 인데 우리는 ihc_* / upc_* 로 보낸다.
  const IHC_UPC_MAP: Record<string, string> = {
    holding_company_entity_name: 'ihc_name',
    holding_company_industry: 'ihc_industry',
    holding_company_country: 'ihc_country',
    holding_company_registration_no: 'ihc_registration_no',
    holding_company_stock_exchange: 'ihc_stock_exchange',
    holding_company_ownership_nature: 'ihc_ownership_structure',
    ultimate_parent_entity_name: 'upc_name',
    ultimate_parent_industry: 'upc_industry',
    ultimate_parent_country: 'upc_country',
    ultimate_parent_registration_no: 'upc_registration_no',
    ultimate_parent_stock_exchange: 'upc_stock_exchange',
    ultimate_parent_ownership_nature: 'upc_ownership_structure',
  };
  for (const [target, source] of Object.entries(IHC_UPC_MAP)) alias(target, source);

  alias('pep_function_nature', 'pep_function');
  alias('pep_function_country', 'pep_country');
  alias('pep_function_nature', 'pep1_pep_function');
  alias('pep_function_country', 'pep1_pep_country');
  alias('pep_name', 'pep1_pep_name');
  alias('pep_period', 'pep1_pep_period');
  alias('source_of_wealth', 'source_of_wealth_info');
  alias('entity_financial_year_end', 'financial_year_end');
  alias('entity_financial_year_end', 'financial_year_end_date');
  alias('entity_financial_year_end', 'entity_proposed_fye');

  // ── Form 45B : 비서 주소 이름 보정 ──
  alias('secretary_residential_address', 'secretary_address');
  // 서명 텍스트는 SG 관례대로 이름으로 채운다(T-Sign 전 표시용)
  alias('secretary_signature', 'secretary_name');

  // ── Constitution Cover : 서명란 2개 (승인권자 이름 기준) ──
  //   원본은 인증 블록이 2개 — 화면의 승인자 1/2 이 각각의 서명란에 들어간다.
  alias('authorised_person_signature_1', 'authorized_person_name');
  alias('authorised_person_signature_1', 'dir1_director_name');
  // Constitution 인증 블록은 1개로 통일 — 2번째(authorised_person_signature_2)는 채우지 않는다.
  //   (화면도 1블록. 채우면 director 2 이름이 새어 2번째 블록이 살아난다.)

  // ── Engagement Letter : 수수료 표 ──────────────────────────────────
  //   화면은 통화와 금액을 별도 키로 받는다(currency_fee_* / fee_*).
  //   템플릿은 한 칸에 [[Fee_Company_Incorporation]] 하나뿐이므로 "SGD 1,200" 처럼 합친다.
  const FEE_MAP: Record<string, string> = {
    fee_company_incorporation: 'fee_incorporation_package',
    fee_nominee_director: 'fee_nominee_director',
    fee_nominee_director_deposit: 'fee_nominee_director_deposit',
    fee_employment_pass: 'fee_employment_pass',
    fee_corppass_opening: 'fee_corppass_opening',
    fee_bank_account_opening: 'fee_bank_account_opening',
    fee_corporate_secretarial_annual: 'fee_annual_secretarial',
    fee_registered_address_annual: 'fee_annual_registered_address',
    fee_nominee_director_annual: 'fee_annual_nominee_director',
    fee_xbrl: 'fee_annual_xbrl',
    fee_corppass_maintenance: 'fee_annual_corppass_maintenance',
    // Incorporation Documents CSSA "Corporate Secretarial Services … [[fee_secretarial_annual]]"
    fee_secretarial_annual: 'fee_annual_secretarial',
  };
  for (const [target, src] of Object.entries(FEE_MAP)) {
    const amount = txt(out[src]);
    if (!amount) continue;
    // 통화 미지정 + 숫자만이면 SGD (Incorporation Documents 템플릿은 '$' 를 따로 두지 않는다)
    const currency = txt(out[`currency_${src}`]) || (/^[\d,.\s]+$/.test(amount) ? 'SGD' : '');
    // ⚠ 멱등: normalize 는 두 번 돈다(baseForm → 문서별 pushOnce). 이미 통화가 붙은 금액에
    //   또 붙이면 "SGD SGD 800" 이 된다(D-5). 통화로 시작하면 그대로 둔다.
    const hasCurrency = !currency || new RegExp(`^${currency.replace(/[$.]/g, '\\$&')}\\s`, 'i').test(amount);
    const merged = hasCurrency ? amount : `${currency} ${amount}`;
    // 원천(src = Engagement Letter 화면 키)이 기준이다. Incorporation Documents 화면도 같은
    //   src 키를 쓰므로, 두 문서가 서로 다른 금액을 갖는 일이 없다(D-6: 예전엔 INCDOC 에서
    //   따로 입력한 값이 Engagement 다음 연도 요금을 덮었다).
    out[target] = merged;
  }
  //   제안서 제목은 **설립할 회사** 이름이다.
  alias('company_name', 'proposed_company_name');

  // ── Form 45 / 45B : 템플릿 변수명이 화면 키와 다른 항목 ───────────────
  //   Form 45  [[Director_Residential_Address]] / [[Consent_Effective_Date]] / [[Dated_On]]
  //   Form 45B [[Secretary_Residential_Address]] / [[Consent_Effective_Date]] / [[Dated_On]]
  alias('director_residential_address', 'director_address');
  alias('secretary_residential_address', 'secretary_address');
  alias('consent_effective_date', 'appointment_effective_date');

  // ── Share Application (신버전) : docx 는 shareholder_* / total_amount_payable 사용 ──
  //   패널은 주주별 스코프에 applicant_* 를 채우므로 신버전 토큰으로 alias.
  //   확장 필드(id_type/nationality/contact_address/email/mobile/currency)는 컨택트 보강에서 채운다.
  alias('shareholder_name', 'applicant_name');
  alias('shareholder_identification_no', 'applicant_identification_no');
  alias('shareholder_residential_address', 'applicant_address');
  alias('shareholder_contact_address', 'applicant_address');
  alias('shareholder_signature', 'applicant_signature');
  alias('shareholder_identification_type', 'applicant_identification_type');
  alias('shareholder_nationality', 'applicant_nationality');
  alias('shareholder_email_address', 'applicant_email');
  alias('shareholder_contact_number', 'applicant_mobile_no');
  alias('total_amount_payable', 'amount_payable');

  //   Dated_On 은 프리셋 전역에서 같은 이름을 쓰기 때문에, 문서 스코프 밖(최상위)의
  //   값이 흘러 들어와 그 문서가 직접 입력받은 일자를 덮어쓴다.
  //   → 이 스코프가 **자기 실행일자를 갖고 있으면** 그 값으로 반드시 덮어쓴다(alias 아님).
  //   ⚠ 이 문서가 **자기 dated_on 을 이미 갖고 있으면 손대지 않는다.**
  //     (Share Application·Notice 등은 스코프에 자기 일자가 있고, First Board 는
  //      패널이 fbod_dated_on 을 넣어 준다. 여기서 덮으면 그 값이 전부 사라진다.)
  if (!isFilled(out.dated_on)) {
    const exDay = txt(out.execution_day);
    const exMonth = txt(out.execution_month);
    const exYear = txt(out.execution_year);
    if (exDay || exMonth || exYear) {
      // "20 day of July, 2026" — 빠진 조각은 건너뛴다
      const head = exDay ? `${exDay} day of ${exMonth}` : exMonth;
      out.dated_on = exYear ? `${head}${head ? ', ' : ''}${exYear}` : head;
    } else if (txt(out.execution_date)) {
      out.dated_on = txt(out.execution_date);
    }
  }

  // ── CAF : [[if directors]] / [[if shareholders]] 반복 행 배열 ──────────
  //   템플릿은 이사·주주 블록 전체를 반복한다. 배열이 없으면 백엔드가 반복을 펼치지
  //   못해 **첫 1명만** 렌더된다(최상위로 접힌 dir1_* 값). dir{N}_* / sh{N}_* 로 만든다.
  if (!Array.isArray(out.directors)) {
    const rows: Record<string, any>[] = [];
    for (const i of rowIds(out, 'dir', 50)) {
      if (!isFilled(out[`dir${i}_director_name`])) continue;
      rows.push({
        director_name: out[`dir${i}_director_name`] || '',
        director_identification_no: out[`dir${i}_director_identification_no`] || '',
        director_nationality: out[`dir${i}_director_nationality`] || '',
        director_tel_no: out[`dir${i}_director_tel_no`] || '',
        // CAF ver-2026 — 신분증 만료일 · 성별(M/F)
        director_id_expiry_date: out[`dir${i}_director_id_expiry_date`] || '',
        director_gender: out[`dir${i}_director_gender`] || '',
        director_mobile_no: out[`dir${i}_director_mobile_no`] || '',
        director_email: out[`dir${i}_director_email`] || '',
        director_date_of_birth: out[`dir${i}_director_date_of_birth`] || '',
        director_address: out[`dir${i}_director_address`] || '',
        is_pep: out[`dir${i}_is_pep`],
      });
    }
    if (rows.length) out.directors = rows;
  }
  // First Board "the appointment of [[first_director_names]] as the first directors" — 이사 전원.
  //   예전 템플릿은 [[first_director_name_1]] and [[first_director_name_2]] 두 칸이라 3번째 이사부터 빠졌다.
  if (Array.isArray(out.directors)) {
    const names = out.directors.map((r: any) => txt(r?.director_name)).filter(Boolean);
    if (!isFilled(out.first_director_names)) {
      out.first_director_names = names.length > 1
        ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
        : (names[0] || '');
    }
    // 이사 1명이면 "as the first director … be and is hereby noted" (단수 문구, N-6)
    if (out.is_single_first_director == null) out.is_single_first_director = names.length === 1;
  }
  // Incorporation Documents ⑦ The Company 서명자 = Signing Director (N-5).
  //   별도 키로 두면 Signing Director 를 바꿔도 노미니 계약만 옛 이름으로 남았다.
  if (isFilled(out.director_name)) out.company_director_name = out.director_name;
  if (!Array.isArray(out.shareholders)) {
    const rows: Record<string, any>[] = [];
    for (const i of rowIds(out, 'sh', 50)) {
      if (!isFilled(out[`sh${i}_shareholder_name`])) continue;
      rows.push({
        shareholder_name: out[`sh${i}_shareholder_name`] || '',
        shareholder_identification_no: out[`sh${i}_shareholder_identification_no`] || '',
        shareholder_number_of_shares: out[`sh${i}_shareholder_number_of_shares`] || '',
        // First Board 결의서의 주주 배정표에 들어가는 주권 번호 (그 화면에서 입력)
        shareholder_share_certificate_no: out[`sh${i}_shareholder_share_certificate_no`] || '',
        // 신버전 First Board 배정표 subscription price 열 (없으면 총 납입액/주식수×단가)
        shareholder_amount_payable: out[`sh${i}_shareholder_amount_payable`]
          || out[`sh${i}_amount_payable`] || '',
        shareholder_mobile_no: out[`sh${i}_shareholder_mobile_no`] || '',
        shareholder_nationality: out[`sh${i}_shareholder_nationality`] || '',
        // CAF ver-2026 — 신분증 만료일 · 성별(M/F)
        shareholder_id_expiry_date: out[`sh${i}_shareholder_id_expiry_date`] || '',
        shareholder_gender: out[`sh${i}_shareholder_gender`] || '',
        shareholder_email: out[`sh${i}_shareholder_email`] || '',
        shareholder_date_of_birth: out[`sh${i}_shareholder_date_of_birth`] || '',
        shareholder_address: out[`sh${i}_shareholder_address`] || '',
        is_pep: out[`sh${i}_is_pep`],
      });
    }
    if (rows.length) out.shareholders = rows;
  }
  // Constitution 서명선(subscriber signature) — 텍스트박스 2줄은 인덱스 키. 주주 배열 앞 2명.
  if (Array.isArray(out.shareholders)) {
    if (!isFilled(out.shareholder_name_1) && out.shareholders[0]) out.shareholder_name_1 = out.shareholders[0].shareholder_name || '';
    if (!isFilled(out.shareholder_name_2) && out.shareholders[1]) out.shareholder_name_2 = out.shareholders[1].shareholder_name || '';
  }

  // ── CAF 연결 당사자(Name(s) of all connected parties) 반복 목록 ────────
  //   템플릿은 SG(CDD) 와 같은 한 줄 반복부를 쓴다 —
  //     [[if managing_directors]] • [[MD_Name]] [[/if managing_directors]]
  //   화면 키(md{N}_md_name 등)를 그 반복부가 읽을 배열로 묶어 준다.
  const CONNECTED: Array<[string, string, string[]]> = [
    ['managing_directors', 'md', ['md_name']],
    ['ceos', 'ceo', ['ceo_name']],
    ['partners', 'pa', ['p_name']],
    ['others', 'ot', ['o_name', 'o_role']],
  ];
  for (const [section, prefix, fields] of CONNECTED) {
    if (Array.isArray(out[section])) continue;
    const rows: Record<string, any>[] = [];
    for (const i of rowIds(out, prefix, 30)) {
      if (!isFilled(out[`${prefix}${i}_${fields[0]}`])) continue;
      const row: Record<string, any> = {};
      for (const f of fields) row[f] = out[`${prefix}${i}_${f}`] || '';
      rows.push(row);
    }
    if (rows.length) out[section] = rows;
  }
  // 같은 표의 Directors 줄은 [[D_Name]] 를 쓴다 — 행마다 채워 준다.
  if (Array.isArray(out.directors)) {
    out.directors = out.directors.map((r: any) => ({ ...r, d_name: r?.d_name ?? r?.director_name ?? '' }));
  }

  // ── CAF : UBO 목록 ────────────────────────────────────────────────────
  //   Aura 표는 항목마다 a) b) c) 슬롯을 두는 서식이다. 반복부로 바꾸면서
  //   **인원 순서대로 a) b) c) 라벨을 데이터로 부여**한다(엔진에 인덱스 개념이 없음).
  const LETTERS = 'abcdefghijklmnopqrstuvwxyz';
  if (!Array.isArray(out.ubos)) {
    const rows: Record<string, any>[] = [];
    for (const i of rowIds(out, 'ubo', 26)) {
      const name = out[`ubo${i}_ubo_name`];
      if (!isFilled(name)) continue;
      const pep = out[`ubo${i}_is_pep`];
      rows.push({
        ubo_label: `${LETTERS[rows.length]})`,
        ubo_name: name || '',
        ubo_identification_no: out[`ubo${i}_ubo_identification_no`] || '',
        ubo_ownership_structure: out[`ubo${i}_ubo_ownership_structure`] || '',
        // UBO 행에 주소가 없으면 같은 사람(이사·주주·컨트롤러)의 주소 — Schedule 1 Principal 표·노미니 계약
        ubo_residential_address:
          out[`ubo${i}_ubo_residential_address`] ||
          personAddressFallback(out, name, out[`ubo${i}_ubo_identification_no`]) ||
          '',
        ubo_nationality: out[`ubo${i}_ubo_nationality`] || '',
        ubo_date_of_birth: out[`ubo${i}_ubo_date_of_birth`] || '',
        ubo_contact_no: out[`ubo${i}_ubo_contact_no`] || '',
        ubo_email: out[`ubo${i}_ubo_email`] || '',
        // 원본의 레거시 체크박스를 대체하는 값
        ubo_pep_yes: toWordBool(pep) ? '☒' : '☐',
        ubo_pep_no: toWordBool(pep) ? '☐' : '☒',
      });
    }
    if (rows.length) out.ubos = rows;
  }
  // 노미니 계약 본문 "[[UBO_Name]] … of [[UBO_Residential_Address]]" — 화면에서 비어 있으면 첫 UBO 행 값(보완 주소 포함)
  if (!isFilled(out.ubo_residential_address) && Array.isArray(out.ubos) && out.ubos[0]) {
    out.ubo_residential_address = out.ubos[0].ubo_residential_address || '';
  }

  // ── CAF Section C : PEP 인물 상세 ─────────────────────────────────────
  //   원본 안내문이 "PEP 가 둘 이상이면 아래 섹션을 복제하라" 이므로 표 전체를 반복한다.
  //   유형 체크박스(싱가포르/외국/국제기구/가족/측근)도 값으로 내려 준다.
  if (!Array.isArray(out.peps)) {
    const rows: Record<string, any>[] = [];
    for (const i of rowIds(out, 'pep', 26)) {
      const name = out[`pep${i}_pep_name`];
      if (!isFilled(name)) continue;
      const cat = txt(out[`pep${i}_pep_category`]).toLowerCase();
      const box = (want: string) => (cat === want ? '☒' : '☐');
      rows.push({
        pep_name: name || '',
        pep_function_country: out[`pep${i}_pep_country`] || '',
        pep_function_nature: out[`pep${i}_pep_function`] || '',
        pep_period: out[`pep${i}_pep_period`] || '',
        pep_relationship: out[`pep${i}_pep_relationship`] || '',
        pep_cat_singapore: box('singapore_pep'),
        pep_cat_foreign: box('foreign_pep'),
        pep_cat_io: box('io_pep'),
        pep_cat_family: box('family'),
        pep_cat_close: box('close_associate'),
      });
    }
    if (rows.length) out.peps = rows;
  }

  // 반복이 한 건도 없을 때를 대비한 최상위 PEP 조건.
  //   flattenIndexedPersonRows 는 dir1_is_pep 을 `is_pep` 으로만 접기 때문에
  //   템플릿의 [[if Is_Director_PEP]] 가 항상 거짓 → **Yes 로 입력해도 No 에 체크**된다.
  if (!isFilled(out.is_director_pep) && isFilled(out.dir1_is_pep)) {
    out.is_director_pep = toWordBool(out.dir1_is_pep);
  }
  if (!isFilled(out.is_shareholder_pep) && isFilled(out.sh1_is_pep)) {
    out.is_shareholder_pep = toWordBool(out.sh1_is_pep);
  }

  // ── CAF : [[if directors]] 반복부 **안쪽** 행별 PEP 조건 ────────────────
  //   템플릿은 [[if Is_Director_PEP]]…[[else]]…[[endif]] 를 directors 행 안에서 쓴다.
  //   우리 행 객체에는 is_pep 만 있어 조건이 항상 거짓이 되므로 행 필드를 채워 준다.
  //   행 안의 [[if Is_Director_PEP]] 는 백엔드가 **그 행의 값으로** 평가한다
  //   (expandRepeatingSections 가 행 복제 시 expandConditionalBlocks 를 행 데이터로 호출).
  //   우리 행 객체에는 is_pep 만 있으므로 조건이 찾는 이름으로 채워 준다.
  //   성별(☐M ☐F)도 같은 방식 — 반복부 안 조건은 단순 [[if 플래그]] 만 쓸 수 있어
  //   (백엔드가 `[[if X == "M"]]` 를 반복부 짝 판정에서 열림으로 안 셈) 행마다 male/female 플래그로 내려 준다.
  const genderFlag = (g: unknown, want: 'M' | 'F') => txt(g).toUpperCase().charAt(0) === want;
  if (Array.isArray(out.directors)) {
    out.directors = out.directors.map((r: any) => ({
      ...r,
      is_director_pep: toWordBool(r?.is_director_pep ?? r?.is_pep),
      is_director_male: genderFlag(r?.director_gender, 'M'),
      is_director_female: genderFlag(r?.director_gender, 'F'),
    }));
  }
  if (Array.isArray(out.shareholders)) {
    out.shareholders = out.shareholders.map((r: any) => ({
      ...r,
      is_shareholder_pep: toWordBool(r?.is_shareholder_pep ?? r?.is_pep),
      is_shareholder_male: genderFlag(r?.shareholder_gender, 'M'),
      is_shareholder_female: genderFlag(r?.shareholder_gender, 'F'),
    }));
  }
  // CAF ver-2026 A3 — Agent / Contact Person PEP 여부 ([[if is_agent_pep]]☒[[else]]☐[[endif]])
  if (isFilled(out.agent_is_pep)) out.is_agent_pep = toWordBool(out.agent_is_pep);

  // ── Notice for Controller : 다른 이름을 쓰는 항목 보정 ──
  alias('controller_name', 'controller_full_name');
  alias('controller_registered_office_address', 'controller_registered_office');
  alias('controller_entity_name', 'controller_corporate_name');
  alias('controller_entity_uen', 'controller_uen');
  alias('confirming_controller_name', 'controller_full_name');

  // ── Notice for Controller (Corporate) / Nominee Director·Shareholder : “No” 면 particulars 비움 ──
  //   템플릿 표 칸은 else 없는 [[if]] 를 쓰면 거짓일 때 칸이 통째로 비어 Word 가 손상되므로
  //   조건 없이 [[값]] 만 두고, 여기서 답이 No 인 블록의 값을 지운다.
  const blankUnless = (flag: string, re: RegExp) => {
    if (!(flag in out) || toWordBool(out[flag])) return;
    for (const k of Object.keys(out)) if (!k.includes('/') && re.test(k)) out[k] = '';
  };
  blankUnless('is_controller', /^controller_(entity_name|entity_uen|registered_office_address|entity_email|entity_contact_no|legal_form|jurisdiction|corporate_register_name|corporate_register_no|date_became_corporate_controller)$/);
  blankUnless('knows_other_controller', /^other_controller_/);
  blankUnless('is_nominee_director', /^nominator_/);
  blankUnless('is_nominee_shareholder', /^nominator_/);

  // 다른 등록가능 컨트롤러 — [[if other_controllers]] 반복 행 (oc{N}_* → 배열)
  if (!Array.isArray(out.other_controllers)) {
    const rows: Record<string, any>[] = [];
    for (let i = 1; i <= 30; i++) {
      const name = out[`oc${i}_other_controller_full_name`];
      if (!isFilled(name)) continue;
      rows.push({
        other_controller_full_name: name || '',
        other_controller_alias: out[`oc${i}_other_controller_alias`] || '',
        other_controller_residential_address: out[`oc${i}_other_controller_residential_address`] || '',
        other_controller_nationality: out[`oc${i}_other_controller_nationality`] || '',
        other_controller_identification_no: out[`oc${i}_other_controller_identification_no`] || '',
        other_controller_date_of_birth: out[`oc${i}_other_controller_date_of_birth`] || '',
        other_controller_date_became_individual_controller:
          out[`oc${i}_other_controller_date_became_individual_controller`] || '',
      });
    }
    if (rows.length) out.other_controllers = rows;
  }
}


/**
 * ACRA Risk Assessment Form — 체크박스(☒/☐) 문자 미리 계산.
 *
 * 원본 레이아웃의 ☐(1글자) 자리에 조건식([[if X == "yes"]]☒[[else]]☐[[endif]])을 넣으면
 * 편집 화면(치환 전)에서 셀이 밀려 이상해 보인다. 대신 상태를 ☒/☐ **한 글자**로
 * 계산해 단일 키(예: b1_q1_yes_chk)로 제공하고, 템플릿은 [[b1_q1_yes_chk]] 만 둔다
 * → 치환 후 1글자라 원본 레이아웃이 그대로 유지된다.
 */
function buildAcraCheckboxes(out: Record<string, any>) {
  const B = (on: boolean) => (on ? '\u2612' : '\u2610'); // ☒ / ☐
  const eq = (k: string, v: string) => String(out[k] ?? '').trim().toLowerCase() === v;

  for (let i = 1; i <= 11; i++) out[`srv_a_${i}_chk`] = B(toWordBool(out[`srv_a_${i}`]));
  for (let i = 1; i <= 19; i++) {
    out[`b1_q${i}_yes_chk`] = B(eq(`b1_q${i}`, 'yes'));
    out[`b1_q${i}_no_chk`] = B(eq(`b1_q${i}`, 'no'));
  }
  for (let i = 1; i <= 5; i++) {
    out[`b2_q${i}_yes_chk`] = B(eq(`b2_q${i}`, 'yes'));
    out[`b2_q${i}_no_chk`] = B(eq(`b2_q${i}`, 'no'));
  }
  for (let i = 1; i <= 11; i++) {
    out[`b3_q${i}_yes_chk`] = B(eq(`b3_q${i}`, 'yes'));
    out[`b3_q${i}_no_chk`] = B(eq(`b3_q${i}`, 'no'));
    out[`b3_q${i}_na_chk`] = B(eq(`b3_q${i}`, 'na'));
  }
  const rating = String(out.customer_risk_rating ?? out.risk_rating ?? '').trim().toLowerCase();
  out.rating_low_chk = B(rating === 'low');
  out.rating_medium_chk = B(rating === 'medium');
  out.rating_high_chk = B(rating === 'high');
  out.rec_recommended_chk = B(eq('recommendation', 'recommended'));
  out.rec_not_recommended_chk = B(eq('recommendation', 'not_recommended'));
  out.appr_approved_chk = B(eq('approval', 'approved'));
  out.appr_not_approved_chk = B(eq('approval', 'not_approved'));
}
